import React from "react";
import { Typography, Link } from "@mui/material";

interface TermsFooterProps {
  /** Custom text before links (default: "By signing in, you agree to our") */
  prefixText?: string;
  /** URL for Terms of Service (optional) */
  termsUrl?: string;
  /** URL for Privacy Policy (optional) */
  privacyUrl?: string;
  /** Handler for Terms of Service click */
  onTermsClick?: () => void;
  /** Handler for Privacy Policy click */
  onPrivacyClick?: () => void;
  /** Custom margin top (default: 4) */
  marginTop?: number | string;
  /** Text alignment (default: "center") */
  align?: "left" | "center" | "right";
}

/**
 * TermsFooter Component
 *
 * Reusable footer component displaying terms of service and privacy policy links.
 * Supports both URL navigation and custom click handlers.
 *
 * Features:
 * - Customizable prefix text
 * - Clickable terms and privacy links with hover effects
 * - Support for external URLs or custom handlers
 * - Consistent purple-themed styling
 * - Responsive text sizing
 *
 * @example
 * // Basic usage with click handlers
 * <TermsFooter
 *   onTermsClick={() => console.log('Terms clicked')}
 *   onPrivacyClick={() => console.log('Privacy clicked')}
 * />
 *
 * @example
 * // With external URLs
 * <TermsFooter
 *   termsUrl="https://example.com/terms"
 *   privacyUrl="https://example.com/privacy"
 * />
 *
 * @example
 * // Custom prefix text
 * <TermsFooter
 *   prefixText="By continuing, you accept our"
 *   align="left"
 * />
 */
const TermsFooter: React.FC<TermsFooterProps> = ({
  prefixText = "By signing in, you agree to our",
  termsUrl,
  privacyUrl,
  onTermsClick,
  onPrivacyClick,
  marginTop = 4,
  align = "center",
}) => {
  const linkStyles = {
    color: "primary.main",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    if (onTermsClick) {
      e.preventDefault();
      onTermsClick();
    }
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    if (onPrivacyClick) {
      e.preventDefault();
      onPrivacyClick();
    }
  };

  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align={align}
      sx={{ mt: marginTop }}
    >
      {prefixText}{" "}
      {termsUrl ? (
        <Link
          href={termsUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkStyles}
        >
          Terms of Service
        </Link>
      ) : (
        <Typography
          component="span"
          variant="body2"
          onClick={handleTermsClick}
          sx={linkStyles}
        >
          Terms of Service
        </Typography>
      )}{" "}
      and{" "}
      {privacyUrl ? (
        <Link
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkStyles}
        >
          Privacy Policy
        </Link>
      ) : (
        <Typography
          component="span"
          variant="body2"
          onClick={handlePrivacyClick}
          sx={linkStyles}
        >
          Privacy Policy
        </Typography>
      )}
    </Typography>
  );
};

export default TermsFooter;
